import { Component, HostListener, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { LogoutOptionsComponent } from './logout-options/logout-options.component';
import { GlobalNavComponent } from './nav/nav.component';
import { LogoComponent } from './logo/logo.component';
import { NgStyle } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { IdleTimeoutService } from 'app/services/idle-timeout.service';
import { IdleWarningDialogComponent } from './idle-warning-dialog/idle-warning-dialog.component';
import { PasswordChangeDialogComponent } from './password-change-dialog/password-change-dialog.component';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app',
    templateUrl: './global.component.html',
    styleUrls: ['./global.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [MatSidenavModule, NgStyle, LogoComponent, GlobalNavComponent, LogoutOptionsComponent, ToolbarComponent, RouterOutlet, TranslateModule]
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private api: ApiService,
    private router: Router,
    private translate: TranslateService,
    public dialog: MatDialog,
    public idleTimeoutService: IdleTimeoutService
  ) { }

  user: any;
  landingPageUser: any;
  loggedCustomer: any;
  language: string;
  userPasswordWillExpire: boolean = false;
  highlightNotification: any;
  copyYear = moment().format('YYYY');
  appVersion = environment.version;
  url: string = this.router.url;
  screenWidth: number;
  sideNavOpened: boolean;
  onMobileScreen: boolean;
  enterpriseName: string;
  showTitles = true;
  initials: string;
  landingPage: any;
  features: string;
  idleWarningDialogRef: any = null;
  private idleSubscription: Subscription;

  // indicator for setting different layout depending on desktop/mobile screen size
  @HostListener('window:resize')
  onResize() {
    this.screenWidth = window.innerWidth;
    if (this.screenWidth > 599) {
      this.onMobileScreen = false;
      this.sideNavOpened = true;
    } else {
      this.onMobileScreen = true;
      this.sideNavOpened = false;
    }
  }

  toggleTitles(titlesShown: boolean) {
    this.showTitles = titlesShown;
  }

  routeChanged(): void {
    this.language = localStorage.getItem("language");
    this.url = this.router.url;
    this.user = JSON.parse(localStorage.getItem("user"));
    this.landingPageUser = JSON.parse(localStorage.getItem("landing-page"));

    if (this.user) {
      moment.tz.setDefault(this.user.timezone)

      if (!localStorage.getItem("features")) {
        this.api.getFeatures(true)
          .subscribe((response: any) => {
            localStorage.setItem("features", JSON.stringify(response));
            this.features = JSON.stringify(response)
          })
      }
      else {
        this.features = localStorage.getItem("features");
      }

      if (!localStorage.getItem("customerInfo")) {
        this.api.getCustomerInformation()
          .subscribe((response: any) => {
            localStorage.setItem("customerInfo", JSON.stringify(response));
          })
      }

      // Calculate if user password will expire within 14 days
      if (this.user.passwordExpirationDays !== 0) {
        if (this.user.passwordChanged && this.user.passwordChanged !== '0001-01-01T00:00:00Z') {
          const passwordWillExpire = moment(this.user.passwordChanged).add(this.user.passwordExpirationDays, 'days');
          if (passwordWillExpire.diff(moment(), 'days') < 14) {
            this.userPasswordWillExpire = true;
          }
        }
      }
    }

    if (localStorage.getItem("language")) {
      if (this.user && !localStorage.getItem("act")) {
        this.api.getActivationTranslations(localStorage.getItem("language"))
          .subscribe((response: any) => {
            localStorage.setItem("act", JSON.stringify(response));
          });
      }
    }
  }

  getUserPasswordExpirationDay(): string {
    return moment(this.user.passwordChanged).add(this.user.passwordExpirationDays, 'days').format('l');
  }

  showPasswordChangeDialog(): void {
    this.dialog.open(PasswordChangeDialogComponent, {
      disableClose: true,
      width: '560px',
      maxWidth: '95vw'
    });
  }

  showIdleWarningDialog(): void {
    this.idleWarningDialogRef = this.dialog.open(IdleWarningDialogComponent, {
      width: '400px',
      disableClose: true
    });

    this.idleWarningDialogRef.afterClosed().subscribe(result => {
      this.idleWarningDialogRef = null;

      if (result === 'continue') {
        // User chose to continue - reset idle state
        this.idleTimeoutService.stopWatching();
        const user = JSON.parse(localStorage.getItem("user"));
        if (user && user.automaticLogoutMinutes) {
          this.idleTimeoutService.startWatching(user.automaticLogoutMinutes);
        }
      }
    });
  }

  ngOnInit(): void {
    this.onResize();
    const language = localStorage.getItem("language");
    if (language) {
      this.translate.use(language);
      //Change englisg language to use UK locale in timestamps
      if (language === 'en') {
        moment.locale('en-GB');
      } else if (language === 'ee') {
        moment.locale('et'); // Estonian: app code 'ee', moment locale 'et'
      } else {
        moment.locale(language);
      }

      if (this.user && !localStorage.getItem("act")) {
        this.api.getActivationTranslations(language)
          .subscribe((response: any) => {
            localStorage.setItem("act", JSON.stringify(response));
          });
      }
    }

    const user = JSON.parse(localStorage.getItem("user"));

    //If user is already logged in start watching for idle timeouts
    if (user && user.automaticLogoutMinutes) {
      this.idleTimeoutService.startWatching(user.automaticLogoutMinutes);
    }

    // Subscribe to idle state changes
    this.idleSubscription = this.idleTimeoutService.onIdleStart$.subscribe(() => {
        this.showIdleWarningDialog();
    });
  }

  ngOnDestroy(): void {
    // Unsubscribe to prevent memory leaks
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }
}
